import MiniSearch from 'minisearch';
import { SEARCH_INDEX_CHUNK_SIZE } from '@/consts/search';
import { NumberOption } from '@/types/generic';
import { FUZZY_SEARCH_OPTIONS, rankByExactMatch } from './search';
import { lastToken, searchFormattedString } from './string';

export interface OptionSearchIndex {
  miniSearch: MiniSearch;
  byId: Map<number, NumberOption>;
}

const OPTION_SEARCH_FIELDS = ['searchLastName', 'searchLabel'] as const;
const OPTION_SEARCH_BOOSTS = { searchLastName: 3, searchLabel: 2 };

const createOptionIndex = (options: NumberOption[]) => {
  const miniSearch = new MiniSearch({
    idField: 'id',
    fields: [...OPTION_SEARCH_FIELDS],
    searchOptions: { boost: OPTION_SEARCH_BOOSTS, ...FUZZY_SEARCH_OPTIONS },
  });

  const byId = new Map<number, NumberOption>();
  options.forEach(option => byId.set(option.value, option));

  const documents = [...byId.values()].map(option => {
    const searchLabel = searchFormattedString(option.label);
    return {
      id: option.value,
      searchLabel,
      searchLastName: lastToken(searchLabel),
    };
  });

  return { miniSearch, byId, documents };
};

export const buildOptionSearchIndex = (options: NumberOption[]): OptionSearchIndex => {
  const { miniSearch, byId, documents } = createOptionIndex(options);
  miniSearch.addAll(documents);
  return { miniSearch, byId };
};

export const buildOptionSearchIndexAsync = async (options: NumberOption[]): Promise<OptionSearchIndex> => {
  const { miniSearch, byId, documents } = createOptionIndex(options);
  await miniSearch.addAllAsync(documents, { chunkSize: SEARCH_INDEX_CHUNK_SIZE });
  return { miniSearch, byId };
};

export const searchOptions = ({ miniSearch, byId }: OptionSearchIndex, query: string, limit: number): NumberOption[] =>
  rankByExactMatch(query, miniSearch.search(searchFormattedString(query)))
    .map(({ id }) => byId.get(id))
    .filter((option): option is NumberOption => option !== undefined)
    .slice(0, limit);
